import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Collapse } from 'antd';
import EmployeeCareerTimeline, {
  EmployeeCareerTimelineHeader,
} from '@/components/employee/EmployeeCareerTimeline';
import EmploymentContractsView from '@/components/employment-contracts/EmploymentContractsView';
import EquipmentPanel from '@/components/equipment-requests/EquipmentPanel';
import OwnEquipmentPanel from '@/components/equipment-requests/OwnEquipmentPanel';
import ZazPanel from '@/components/zaz/ZazPanel';
import HolidayHistoryView from '@/components/holiday-history/HolidayHistoryView';
import { ResourceHistory } from '@/components/resource-history';
import { isHlbStaffRoles } from '@/components/team-assignments/companyListFilter';
import { ROUTES } from '@/constants';
import CompensationPanel, {
  COMPENSATION_QUERY_KEY,
} from '@/components/compensation/CompensationPanel';
import { getEmployeeCompensation } from '@/services/compensation.service';
import { getTeamPersonSummary } from '@/services/teamAssignments.service';
import { useAuthStore } from '@/store/useAuthStore';
import EmployeeLeavesPanel from './EmployeeLeavesPanel';
import EmployeeTeamPanel from './EmployeeTeamPanel';
import EmploymentActionsCard from './EmploymentActionsCard';

/**
 * What the details panel shows around the form for each tile: views that save
 * on their own (contracts, histories) and shortcuts.
 *
 * @returns {{ before: Object<string, React.ReactNode>, after: Object<string, React.ReactNode> }}
 */
export default function useEmployeePanelExtras({
  employee,
  employeeId,
  access,
  hasHrFlows,
  canExtendFromHere,
  onOpenRequest,
  onOpenTile,
  onboarding = null,
  zaz = null,
}) {
  const { t } = useTranslation(['translations', 'teamAssignments']);
  const roles = useAuthStore((state) => state.user?.roles) || [];

  // Same query key as the header's team button: one request. Null for anyone
  // outside the HLB team, so the tile then does not exist.
  const { data: team } = useQuery({
    queryKey: ['team-person-summary', undefined, employeeId],
    queryFn: () => getTeamPersonSummary({ employeeId }),
    enabled: isHlbStaffRoles(roles) && !!employeeId,
    retry: false,
  });
  // The server decides who sees the salary (HLB, the company's picked salary
  // viewers, the employee). Refused means no tile.
  const { data: compensation } = useQuery({
    queryKey: [COMPENSATION_QUERY_KEY, employeeId],
    queryFn: () => getEmployeeCompensation(employeeId),
    enabled: !!employeeId,
    retry: false,
  });
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  // Each head links to their own profile when they have one.
  const supervisors = (employee?.supervisors || []).filter((s) => s?.fullName);
  const companyId = employee?.company?._id || employee?.company;

  const before = {
    org: supervisors.length > 0 && (
      <p className="m-0 text-[13px] text-zinc-600">
        <span className="font-bold text-zinc-700">{tp('supervisor')}:</span>{' '}
        {supervisors.map((s, i) => (
          <Fragment key={s._id}>
            {i > 0 && ', '}
            {s.employeeId ? (
              <Link
                className="font-semibold underline"
                to={`${ROUTES.admin}/${ROUTES.employees}/${s.employeeId}${
                  companyId ? `?company=${companyId}` : ''
                }`}
              >
                {s.fullName}
              </Link>
            ) : (
              s.fullName
            )}
          </Fragment>
        ))}
      </p>
    ),
    ugovor: access.canManageEmployment && (
      <EmploymentActionsCard
        employee={employee}
        canExtendFromHere={canExtendFromHere}
      />
    ),
    go: hasHrFlows && onOpenRequest && (
      <div className="flex">
        <Button
          className="btn-default btn-default--hover !rounded-[50px]"
          onClick={onOpenRequest}
        >
          {tp('holidayUi.newHolidayRequest')}
        </Button>
      </div>
    ),
    odsustva: hasHrFlows && onOpenRequest && (
      <div className="flex">
        <Button
          className="btn-default btn-default--hover !rounded-[50px]"
          onClick={onOpenRequest}
        >
          {tp('newRequest')}
        </Button>
      </div>
    ),
  };

  const after = {
    tim: team && <EmployeeTeamPanel summary={team} />,
    oprema:
      onboarding?.showEquipmentTile &&
      (onboarding.isOwnEquipment ? (
        <OwnEquipmentPanel
          equipment={onboarding.equipment}
          onRequest={onboarding.openEquipment}
        />
      ) : (
        <EquipmentPanel
          equipment={onboarding.equipment}
          employeeId={employeeId}
          onRequest={onboarding.openEquipment}
        />
      )),
    zaz: zaz?.showTile && (
      <ZazPanel
        employeeId={employeeId}
        zaz={zaz.zaz}
        onSend={zaz.canSend ? zaz.openSend : null}
        // The existing equipment request, untouched: only a shortcut to it.
        onRequestEquipment={
          onboarding?.canRequestEquipment && !onboarding.isOwnEquipment
            ? onboarding.openEquipment
            : null
        }
      />
    ),
    zarada: compensation && <CompensationPanel data={compensation} />,
    ugovor: (
      <div className="mt-3 grid gap-4">
        <section className="grid gap-3 rounded-xl border border-zinc-200 bg-white p-3">
          <EmployeeCareerTimelineHeader />
          <EmployeeCareerTimeline employeeId={employeeId} compact />
        </section>
        {access.canSeeContracts && (
          <div>
            <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
              {t('employmentContracts.title', { ns: 'translations' })}
            </h3>
            <EmploymentContractsView
              employeeId={employeeId}
              guideCollapsed
              canEdit
              profileEmploymentType={
                employee?.employmentSettings?.employmentType
              }
            />
          </div>
        )}
      </div>
    ),
    // Long (balance chart + events), so it opens on demand.
    go: access.canSeeLeaveHistory && (
      <Collapse
        className="mt-3"
        items={[
          {
            key: 'go-history',
            label: (
              <span className="text-[13px] font-bold">
                {t('holidayHistory.title', { ns: 'translations' })}
              </span>
            ),
            children: <HolidayHistoryView employeeId={employeeId} />,
          },
        ]}
      />
    ),
    odsustva: access.canSeeLeaveHistory && (
      <EmployeeLeavesPanel
        employeeId={employeeId}
        onOpenHoliday={() => onOpenTile('go')}
      />
    ),
    istorija: (
      <ResourceHistory
        modelName="Employee"
        documentId={employeeId}
        variant="profile"
        createdAt={employee?.createdAt}
      />
    ),
  };

  return {
    before,
    after,
    team: team || null,
    compensation: compensation || null,
  };
}
